import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { JUSTIFICATIVA_MAXIMO, validarJustificativa } from '@malgoos/shared';
import { Botao } from '../../componentes/base';
import { AreaSensivel, Rotulo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { traduzirErro } from '../../nucleo/api';
import { CLASSE_AREA } from './ConcluirAtividades';

/**
 * Lista B de 30/10 (item 13): cancelar e excluir tarefa pedem a justificativa. O pop-up segue o padrão dos diálogos de
 * confirmação (Modal com Cancelar e a ação); o texto passa pelo mesmo `validarJustificativa` do servidor (`packages/shared`).
 * O estado fica no conteúdo, que só existe com o diálogo aberto: ao reabrir o campo volta limpo.
 */
export function DialogoJustificativa({
  aberto,
  aoFechar,
  aoConfirmar,
  titulo,
  texto,
  botao,
  carregando,
  nomesNoTexto,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoConfirmar: (justificativa: string) => void;
  titulo: string;
  texto: ReactNode;
  /** Rótulo do botão de confirmar (a ação). */
  botao: string;
  carregando?: boolean;
  nomesNoTexto?: (string | null | undefined)[];
}) {
  if (!aberto) return null;
  return <Conteudo aoFechar={aoFechar} aoConfirmar={aoConfirmar} titulo={titulo} texto={texto} botao={botao} carregando={carregando} nomesNoTexto={nomesNoTexto} />;
}

function Conteudo({ aoFechar, aoConfirmar, titulo, texto, botao, carregando, nomesNoTexto }: Omit<Parameters<typeof DialogoJustificativa>[0], 'aberto'>) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const id = useId();
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const confirmar = () => {
    const r = validarJustificativa(valor);
    if (!r.ok) return setErro(traduzirErro(r.erros[0]?.codigo, r.erros[0]?.mensagem ?? ''));
    aoConfirmar(r.valor);
  };
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="perigo" carregando={carregando} onClick={confirmar} data-confirmar-justificativa="">
            {botao}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm" data-dialogo-justificativa="">
        <div className="text-tinta-2">{texto}</div>
        <Rotulo id={`${id}-j`} rotulo={t('crm.form.justificativa')} obrigatorio erro={erro ?? undefined} ajuda={t('crm.form.justificativaAjuda')}>
          <AreaSensivel
            nomesNoTexto={nomesNoTexto}
            rotulo={t('crm.form.justificativa')}
            id={`${id}-j`}
            name="justificativa"
            rows={4}
            maxLength={JUSTIFICATIVA_MAXIMO}
            className={CLASSE_AREA}
            value={valor}
            aria-invalid={!!erro || undefined}
            onChange={(e) => {
              setValor(e.target.value);
              setErro(null);
            }}
          />
        </Rotulo>
      </div>
    </Modal>
  );
}
